import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { templatesApi } from '../api';
import { ComplianceTemplateDto } from '../api/complianceStructureBuilder.dto';
import ConfirmationModal from '@/components/ConfirmationModal';
import PageInfoButton from '@/components/PageInfoButton';
import { notify } from '@core/notifications/notify';
import { useModal, FrameworkAssignmentWizard } from '@components/modal';
import {
  FileText,
  Search,
  Edit,
  Trash2,
  CheckCircle2,
  Clock,
  Archive,
  RefreshCw,
  AlertTriangle,
  ChevronDown,
  Plus,
  Copy,
  ArrowRight,
  Folder,
  Tag,
  Layers,
  Wand2,
  Download,
  Upload,
} from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  if (status === 'published') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] bg-emerald-100 text-emerald-800 border border-emerald-200">
        <CheckCircle2 size={12} /> Published
      </span>
    );
  }
  if (status === 'draft') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] bg-amber-100 text-amber-800 border border-amber-200">
        <Clock size={12} /> Draft
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] bg-slate-100 text-slate-600 border border-slate-200">
      <Archive size={12} /> Archived
    </span>
  );
}

function EmptyState({ onCreate, onWizard }: { onCreate: () => void; onWizard: () => void }) {
  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-12">
      <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
        <FileText className="text-blue-500" size={40} />
      </div>
      <h3 className="text-xl font-bold text-slate-800 mb-2">No Templates Yet</h3>
      <p className="text-slate-500 max-w-md mb-8">
        Templates define the compliance pages companies fill out. Build one in the Structure Builder.
      </p>
      <div className="flex items-center justify-center gap-3">
        <button onClick={onWizard} className="inline-flex items-center gap-2 px-5 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-colors shadow-sm">
          <Wand2 size={18} /> Quick Assign Wizard
        </button>
        <button onClick={onCreate} className="inline-flex items-center gap-2 px-5 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-sm">
          <Plus size={18} /> Build New Template
        </button>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const Templates: React.FC = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const { openModal } = useModal();

  const openWizard = () => openModal({ component: FrameworkAssignmentWizard, props: { onComplete: () => {} }, options: { size: 'large' } });

  const [templates, setTemplates] = useState<ComplianceTemplateDto[]>([]);
  const [templateStats, setTemplateStats] = useState<Map<number, { nodes: number; fields: number }>>(new Map());
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [expandedTemplates, setExpandedTemplates] = useState<Set<number>>(() => {
    try {
      const saved = localStorage.getItem('tpl_expanded');
      return saved ? new Set(JSON.parse(saved) as number[]) : new Set<number>();
    } catch { return new Set<number>(); }
  });

  // Duplicate modal state
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ComplianceTemplateDto | null>(null);
  const [duplicateTemplateName, setDuplicateTemplateName] = useState('');
  const [duplicateAsDraft, setDuplicateAsDraft] = useState(false);

  // Delete modal state
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; templateId: number | null; templateName: string; preview: any | null }>({ open: false, templateId: null, templateName: '', preview: null });
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importName, setImportName] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const importFileInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await templatesApi.getTemplates();
      setTemplates(data || []);

      // Load stats for each template
      const statsMap = new Map<number, { nodes: number; fields: number }>();
      await Promise.all((data || []).map(async (template) => {
        try {
          const response = await fetch(`/api/compliance/definition/templates/${template.id}/stats`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          const result = await response.json();
          if (result.success) {
            statsMap.set(template.id, result.data);
          } else {
            statsMap.set(template.id, { nodes: 0, fields: 0 });
          }
        } catch {
          statsMap.set(template.id, { nodes: 0, fields: 0 });
        }
      }));
      setTemplateStats(statsMap);

      // Only expand the first item on first load
      const firstTemplate = data[0];
      if (!localStorage.getItem('tpl_expanded') && firstTemplate) {
        const expanded = new Set<number>([firstTemplate.id]);
        setExpandedTemplates(expanded);
        localStorage.setItem('tpl_expanded', JSON.stringify(Array.from(expanded)));
      }
    } catch (error) {
      notify.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (template.description && template.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = !statusFilter || template.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const toggleExpand = (id: number) => {
    setExpandedTemplates(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      localStorage.setItem('tpl_expanded', JSON.stringify(Array.from(next)));
      return next;
    });
  };

  // ─── Handlers ──────────────────────────────────────────────────────────

  const handleEditTemplate = (template: ComplianceTemplateDto) => {
    navigate(`/compliance/definition/builder?templateId=${template.id}`);
  };

  const handleDuplicateTemplate = async () => {
    if (!selectedTemplate) return;
    try {
      const csrfToken = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
      const response = await fetch(`/api/compliance/definition/templates/${selectedTemplate.id}/duplicate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          name: duplicateTemplateName || `Copy of ${selectedTemplate.name}`,
          status: duplicateAsDraft ? 'draft' : 'published'
        })
      });
      if (response.ok) {
        setShowDuplicateModal(false);
        setDuplicateTemplateName('');
        notify.success('Template duplicated successfully');
        await loadTemplates();
      } else {
        const error = await response.json();
        notify.error(error.error || 'Failed to duplicate template');
      }
    } catch (error) {
      notify.error('Failed to duplicate template');
    }
  };

  const handleUpdateTemplateStatus = async (templateId: number, status: string) => {
    try {
      await templatesApi.updateTemplate(templateId, { status });
      notify.success(`Template ${status === 'published' ? 'published' : status === 'archived' ? 'archived' : 'updated'}`);
      await loadTemplates();
    } catch (error) {
      notify.error('Failed to update template status');
    }
  };

  const openDeleteConfirm = async (templateId: number, templateName: string) => {
    // Load preview so the confirmation shows what cascades
    try {
      const preview = await templatesApi.previewDeleteTemplate(templateId);
      setDeleteConfirm({ open: true, templateId, templateName, preview });
    } catch {
      setDeleteConfirm({ open: true, templateId, templateName, preview: null });
    }
  };

  const confirmDeleteTemplate = async () => {
    if (!deleteConfirm.templateId) return;
    setDeleteLoading(true);
    try {
      const cascade = !!deleteConfirm.preview?.boundFrameworkVersion;
      await templatesApi.deleteTemplate(deleteConfirm.templateId, cascade);
      notify.success(cascade ? 'Template and associated framework deleted' : 'Template deleted');
      await loadTemplates();
    } catch (error: any) {
      const msg = error?.response?.data?.error || error?.message || 'Failed to delete template';
      notify.error(msg);
    } finally {
      setDeleteLoading(false);
      setDeleteConfirm({ open: false, templateId: null, templateName: '', preview: null });
    }
  };

  const handleExportTemplate = async (template: ComplianceTemplateDto) => {
    try {
      const response = await fetch(`/api/compliance/definition/templates/${template.id}/export`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const result = await response.json();
      if (!result.success || !result.data) {
        notify.error('Failed to export template');
        return;
      }
      const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${template.name.replace(/[^a-zA-Z0-9_-]/g, '_')}_export.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      notify.success('Template exported successfully');
    } catch {
      notify.error('Failed to export template');
    }
  };

  const handleImportTemplate = async () => {
    if (!importFile) return;
    setImportLoading(true);
    try {
      const text = await importFile.text();
      let exportData: any;
      try {
        exportData = JSON.parse(text);
      } catch {
        notify.error('Invalid JSON file');
        setImportLoading(false);
        return;
      }

      if (!exportData._exportVersion || !exportData.template) {
        notify.error('This file is not a valid template export');
        setImportLoading(false);
        return;
      }

      const csrfToken = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
      const response = await fetch('/api/compliance/definition/templates/import', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          exportData,
          name: importName || undefined,
        }),
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setShowImportModal(false);
        setImportFile(null);
        setImportName('');
        notify.success(`Template imported — ${result.data.nodesImported} nodes, ${result.data.fieldsImported} fields`);
        await loadTemplates();
      } else {
        notify.error(result.error || 'Failed to import template');
      }
    } catch {
      notify.error('Failed to import template');
    } finally {
      setImportLoading(false);
    }
  };

  // ─── Derived data ──────────────────────────────────────────────────────

  const stats = {
    total: templates.length,
    published: templates.filter(t => t.status === 'published').length,
    draft: templates.filter(t => t.status === 'draft').length,
    archived: templates.filter(t => t.status === 'archived').length,
  };

  // ─── Loading / Permission states ───────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="text-blue-600 animate-spin" size={40} />
      </div>
    );
  }

  if (!hasPermission('templates.read')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-red-200 p-8 max-w-md">
          <AlertTriangle className="text-red-500 mb-4" size={48} />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-slate-600">You don't have permission to access Templates.</p>
        </div>
      </div>
    );
  }

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center">
              <h1 className="text-3xl font-bold text-slate-900 text-left">Compliance Templates</h1>
              <PageInfoButton />
            </div>
            <p className="text-slate-500 text-sm mt-0.5 text-left">Templates define the compliance pages companies fill out — each linked to a framework version</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={openWizard} className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white font-medium text-sm rounded-lg hover:bg-indigo-700 transition-colors shadow-sm">
              <Wand2 size={16} /> Quick Assign
            </button>
            <button onClick={loadTemplates} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-slate-700 font-medium text-sm rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
              <RefreshCw size={16} /> Refresh
            </button>
            {hasPermission('templates.create') && (
              <button onClick={() => setShowImportModal(true)} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-slate-700 font-medium text-sm rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
                <Upload size={16} /> Import
              </button>
            )}
            {hasPermission('templates.create') && (
              <button onClick={() => navigate('/compliance/definition/builder')} className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors shadow-sm">
                <Plus size={16} /> Build New Template
              </button>
            )}
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider text-left">Total Templates</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{stats.total}</p>
          </div>
          <div className="bg-white rounded-xl border border-emerald-200 p-4 bg-emerald-50/50">
            <p className="text-xs font-medium text-emerald-700 uppercase tracking-wider text-left">Published</p>
            <p className="text-2xl font-bold text-emerald-800 mt-1">{stats.published}</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider text-left">Draft</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{stats.draft}</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider text-left">Archived</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{stats.archived}</p>
          </div>
        </div>

        {/* Search + Filter */}
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 text-sm"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
          >
            <option value="">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {/* Template List — collapsible rows like Frameworks */}
        {filteredTemplates.length === 0 ? (
          <EmptyState onCreate={() => navigate('/compliance/definition/builder')} onWizard={openWizard} />
        ) : (
          <div className="space-y-2">
            {filteredTemplates.map(template => {
              const isExpanded = expandedTemplates.has(template.id);
              const isPublished = template.status === 'published';
              const isDraft = template.status === 'draft';
              const stats = templateStats.get(template.id) || { nodes: 0, fields: 0 };
              const source = template.generation_source || 'manual';
              const isAi = source.startsWith('ai');
              const isParser = source.includes('parser') || source.includes('document');
              const isDemo = source.includes('demo');
              const sourceLabel = isParser ? 'Parser Import' : isAi ? 'AI Generated' : isDemo ? 'Demo Data' : 'Manual Build';
              const sourceColor = isParser ? 'bg-sky-100 text-sky-700 border-sky-200' : isAi ? 'bg-violet-100 text-violet-700 border-violet-200' : isDemo ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-slate-100 text-slate-600 border-slate-200';
              const sourceIcon = isParser ? '⇥' : isAi ? '✦' : isDemo ? '◉' : '✎';

              return (
                <div key={template.id} className="bg-white rounded-3xl border border-slate-100 overflow-hidden">

                  {/* Template parent row */}
                  <div
                    className={`flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                      isPublished ? 'border-l-4 border-l-emerald-500' : isDraft ? 'border-l-4 border-l-amber-400' : 'border-l-4 border-l-slate-200'
                    }`}
                    onClick={() => toggleExpand(template.id)}
                  >
                    {/* Expand icon */}
                    <ChevronDown size={16} className={`text-slate-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />

                    {/* Icon */}
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isPublished ? 'bg-emerald-100' : isDraft ? 'bg-amber-100' : 'bg-slate-100'
                    }`}>
                      <FileText size={18} className={isPublished ? 'text-emerald-600' : isDraft ? 'text-amber-600' : 'text-slate-400'} />
                    </div>

                    {/* Name */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-900 truncate text-left">{template.name}</p>
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold border ${sourceColor}`}>
                          {sourceIcon} {sourceLabel}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 truncate text-left">
                        {template.creator_name ? `by ${template.creator_name}` : ''}{template.creator_name && template.description ? ' · ' : ''}{template.description || ''}
                      </p>
                    </div>

                    {/* Status badge */}
                    <StatusBadge status={template.status} />

                    {/* Metadata */}
                    <span className="text-xs text-slate-400 flex-shrink-0">v{template.version}</span>
                    <span className="text-xs text-slate-400 flex-shrink-0">{stats.nodes} nodes, {stats.fields} fields</span>
                    <span className="text-xs text-slate-400 flex-shrink-0">{new Date(template.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>

                    {/* Actions — hidden when expanded (shown inside panel instead) */}
                    {!isExpanded && (
                      <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => handleEditTemplate(template)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors"
                        >
                          <Edit size={13} /> Edit
                        </button>
                        {isDraft && hasPermission('templates.update') && (
                          <button
                            onClick={() => handleUpdateTemplateStatus(template.id, 'published')}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg border border-emerald-200 transition-colors"
                          >
                            <CheckCircle2 size={13} /> Publish
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Expanded detail panel */}
                  {isExpanded && (
                    <div className="border-t border-slate-100 bg-slate-50/50">
                      <div className="ml-14 mr-5 my-3 grid grid-cols-4 gap-3">
                        {/* Info card: Version */}
                        <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
                          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                            <Tag size={12} /> Version
                          </div>
                          <p className="text-sm font-semibold text-slate-800">v{template.version}</p>
                        </div>

                        {/* Info card: Structure */}
                        <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
                          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                            <Layers size={12} /> Structure
                          </div>
                          <p className="text-sm font-semibold text-slate-800">{stats.nodes} nodes, {stats.fields} fields</p>
                        </div>

                        {/* Info card: Created */}
                        <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
                          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                            <Clock size={12} /> Created
                          </div>
                          <p className="text-sm font-semibold text-slate-800">{new Date(template.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                          <p className="text-[10px] text-slate-400 mt-0.5">{new Date(template.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}{template.creator_name ? ` by ${template.creator_name}` : ''}</p>
                        </div>

                        {/* Info card: Source */}
                        <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
                          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                            <Folder size={12} /> Source
                          </div>
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold border ${sourceColor}`}>
                            {sourceIcon} {sourceLabel}
                          </span>
                        </div>
                      </div>

                      {/* Next step hint */}
                      {isPublished && (
                        <div className="ml-14 mr-5 mb-3 flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5">
                          <span className="text-sm text-emerald-800 font-medium">Published — already mapped via clause references and ready for assignment from the Frameworks page</span>
                          <button
                            onClick={() => navigate('/compliance/definition/frameworks')}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
                          >
                            Back to Frameworks <ArrowRight size={13} />
                          </button>
                        </div>
                      )}
                      {isDraft && (
                        <div className="ml-14 mr-5 mb-3 flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
                          <span className="text-sm text-amber-800 font-medium">Draft — open in the builder to add nodes and fields, then publish</span>
                          <button
                            onClick={() => handleEditTemplate(template)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors"
                          >
                            Open Builder <ArrowRight size={13} />
                          </button>
                        </div>
                      )}

                      {/* Template actions — shown inside panel when expanded */}
                      <div className="ml-14 mr-5 mb-3 flex items-center gap-2">
                        {isDraft && hasPermission('templates.update') && (
                          <button
                            onClick={() => handleUpdateTemplateStatus(template.id, 'published')}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg border border-emerald-200 transition-colors"
                          >
                            <CheckCircle2 size={13} /> Publish
                          </button>
                        )}
                        {hasPermission('templates.update') && (
                          <button
                            onClick={() => {
                              setSelectedTemplate(template);
                              setDuplicateTemplateName(isPublished ? `Draft of ${template.name}` : `Copy of ${template.name}`);
                              setDuplicateAsDraft(isPublished);
                              setShowDuplicateModal(true);
                            }}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-700 bg-white hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors"
                          >
                            <Copy size={13} /> Duplicate
                          </button>
                        )}
                        {template.status !== 'archived' && hasPermission('templates.update') && (
                          <button
                            onClick={() => handleUpdateTemplateStatus(template.id, 'archived')}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-700 bg-white hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors"
                          >
                            <Archive size={13} /> Archive
                          </button>
                        )}
                        <button
                          onClick={() => handleExportTemplate(template)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-700 bg-white hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors"
                        >
                          <Download size={13} /> Export
                        </button>
                        {isDraft && hasPermission('templates.delete') && (
                          <button
                            onClick={() => openDeleteConfirm(template.id, template.name)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg border border-red-200 transition-colors"
                          >
                            <Trash2 size={13} /> Delete
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Duplicate Template Modal */}
      {showDuplicateModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowDuplicateModal(false)}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-900 mb-4">{duplicateAsDraft ? 'Create Draft Copy' : 'Duplicate Template'}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Template Name</label>
                <input
                  type="text"
                  value={duplicateTemplateName}
                  onChange={(e) => setDuplicateTemplateName(e.target.value)}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 text-sm"
                  placeholder="Enter template name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                <select
                  value={duplicateAsDraft ? 'draft' : 'published'}
                  onChange={(e) => setDuplicateAsDraft(e.target.value === 'draft')}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 text-sm"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowDuplicateModal(false); setDuplicateTemplateName(''); setSelectedTemplate(null); }}
                className="flex-1 px-4 py-2.5 bg-white text-slate-700 font-medium text-sm rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDuplicateTemplate}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
              >
                {duplicateAsDraft ? 'Create Draft Copy' : 'Duplicate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, templateId: null, templateName: '', preview: null })}
        onConfirm={confirmDeleteTemplate}
        title={deleteConfirm.preview?.boundFrameworkVersion ? 'Cascade Delete Template + Framework' : 'Delete Template'}
        message={(() => {
          const p = deleteConfirm.preview;
          if (!p) return `Are you sure you want to delete "${deleteConfirm.templateName}"? This cannot be undone.`;
          const lines: string[] = [`You are about to delete "${deleteConfirm.templateName}".`];
          lines.push(`• ${p.nodes} template nodes`);
          lines.push(`• ${p.fields} evidence fields`);
          if (p.boundFrameworkVersion) {
            lines.push('');
            lines.push(`This template is bound to a framework. The following will ALSO be deleted:`);
            lines.push(`• Framework: "${p.boundFrameworkVersion.framework_name}"`);
            lines.push(`• Framework version: v${p.boundFrameworkVersion.version}`);
            if (p.mappings > 0) lines.push(`• ${p.mappings} framework mappings`);
            if (p.additionalAssignedCompanies > 0) {
              lines.push('');
              lines.push(`⚠️ WARNING: ${p.additionalAssignedCompanies} company assignment(s) will be removed along with their compliance pages and evidence. This affects real company data.`);
            }
          }
          lines.push('');
          lines.push('This cannot be undone.');
          return lines.join('\n');
        })()}
        confirmText={deleteConfirm.preview?.boundFrameworkVersion ? 'Cascade Delete Everything' : 'Delete Template'}
        cancelText="Cancel"
        type="danger"
        loading={deleteLoading}
      />

      {/* Import Template Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setShowImportModal(false); setImportFile(null); setImportName(''); }}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-900 mb-1">Import Template</h3>
            <p className="text-sm text-slate-500 mb-4">Upload a previously exported template JSON file to restore it.</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Template File</label>
                <input
                  ref={importFileInputRef}
                  type="file"
                  accept=".json"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setImportFile(file);
                    e.currentTarget.value = '';
                  }}
                />
                <button
                  onClick={() => importFileInputRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg text-sm text-slate-600 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                >
                  <Upload size={16} />
                  {importFile ? importFile.name : 'Choose JSON file...'}
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Template Name (optional)</label>
                <input
                  type="text"
                  value={importName}
                  onChange={(e) => setImportName(e.target.value)}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 text-sm"
                  placeholder="Leave blank to use original name"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowImportModal(false); setImportFile(null); setImportName(''); }}
                className="flex-1 px-4 py-2.5 bg-white text-slate-700 font-medium text-sm rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleImportTemplate}
                disabled={!importFile || importLoading}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                {importLoading ? (
                  <span className="inline-flex items-center gap-2"><RefreshCw size={14} className="animate-spin" /> Importing...</span>
                ) : (
                  <span className="inline-flex items-center gap-2"><Upload size={14} /> Import Template</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Templates;
